import { Event as MyonEvent, EventListener, View, ViewData, CollectionView } from "silver";
import { html } from "core/Controls/Books/BookModal.html";
import { Tabset } from "core/Controls/Tabset";
import { Book as BookData } from "core/Data/Book";
import { BookReviewService, BookReview } from "core/Services/BookReviewService";
import { SearchBooksService } from "core/Services/SearchBooksService";
import { BooksLang, UserLang } from "Lang";
import { Role } from "shared/Api/Role";
import { generateId } from "core/Misc";
import { BookMinimumGradeAccessService } from "core/Services/BookMinimumGradeAccessService";
import { UserLangGrade, UserLangGradeShort } from "shared/LanguageExtensions";

interface SearchSimilarButton {
	label: string;
	value: string|number|null;
	href: string|null;
}

type BookModalAction = {
	label:         Node;
	icon:          Node;
	action:        (e: Event) => void;
	modifierClass: string;
};

interface BookModalViewData extends ViewData {
	gradeRangeString: string;
	id: string;
	book: BookData;
	showRating: boolean;
	bookCoverAlt: string;
	/** Toggles the grade level info panel under the book cover */
	showGradeLevelInfo: boolean;
	/** Shows the message "All book management settings have been disabled." */
	showLibraryManagementDisabled: boolean;
	/** Shows the select control for the book's grade minimum */
	showAdminGradeMin: boolean;
	showArQuizButton: boolean;

	similarButtons: SearchSimilarButton[];
	reviews: BookReview[];
	reviewsLoading: boolean;
	bookModificationMessage: string|null;
	collapseBookDescription: boolean;
	bookLang: BooksLang;
	userLang: UserLang;

	primaryActions: BookModalAction[];
	secondaryActions: BookModalAction[];
}

interface BookModalView extends View<BookModalViewData> {
	overviewButton: HTMLButtonElement;
	detailsButton: HTMLButtonElement;
	reviewsButton: HTMLButtonElement;
	gradeMin: HTMLSelectElement;
	tabContainer: HTMLElement;
	tabContent: HTMLElement;
	tablist: HTMLElement;
	overviewTab: HTMLElement;
	detailsTab: HTMLElement;
	reviewsTab: HTMLElement;

	primaryActions: CollectionView<BookModalAction>;
	secondaryActions: CollectionView<BookModalAction>;
}

export interface BookModalEvent extends MyonEvent {
	sender: BookModal;
	originalEvent?: Event;
}

export class BookModal {

	public readonly onClose = EventListener.create<BookModalEvent>("close");
	public readonly onRequestArQuizLaunch = EventListener.create<BookModalEvent>("requestArQuizLaunch");
	public readonly onRequestReviewData = EventListener.create<BookModalEvent>("requestReviewData");

	private view: BookModalView;
	private hasReviewData: boolean = false;
	private tabset: Tabset;

	constructor(
		private bookReviewService: BookReviewService,
		private searchBooksService: SearchBooksService,
		private bookMinimumGradeAccessService: BookMinimumGradeAccessService,
		public readonly book: BookData,
		private userId: number,
		private userRoleId: Role,
		private canManageBookGradeLevelRestrictions: boolean,
		isSharedLogin: boolean,
		private bookLang: BooksLang,
		private userLang: UserLang
	) {
		this.view = new View<BookModalViewData>(html, {
			valueOrDash: (value: string|null): string => {
				if (! value) {
					return "—";
				}
				return value;
			},
			hasQuiz: (value: boolean): string => {
				return value ? bookLang.LABEL_HAS_QUIZ_YES() : bookLang.LABEL_HAS_QUIZ_NO();
			},
			formatAtos: (value: number|null): string => {
				if (! value) {
					return "—";
				}
				return value.toFixed(1);
			},
			toInt(value) { return Number(value).toString(); },

			getRatingLang: (rating: number): string => {
				if (rating === 0) {
					return bookLang.NO_RATING_LABEL();
				}
				const rounded = Math.round(rating * 10) / 10;
				return bookLang.RATING_LABEL(rounded);
			},

			getRatingPercentage: (rating: number): string => {
				return (100 * Math.max(0, Math.min(1, rating / 5))) + "%";
			},
			handleGradeMinChange: () => {
				this.book.minimumGradeBookAccess = Number(this.view.gradeMin.value);
				this.bookMinimumGradeAccessService
					.set(this.book.bookId, Number(this.view.gradeMin.value))
					.then(({ modified }) => {
						this.book.minGradeBookLastModified = modified;
					});
			},
			handleActionClick:       (action: (e: Event) => any, e: Event) => action(e),
			handleArQuizButtonClick: (originalEvent) => {
				this.onRequestArQuizLaunch.trigger({ sender: this, originalEvent });
			},
			handleCloseButtonClick: () => {
				this.onClose.trigger({ sender: this });
			},
			sortReviews: (a: BookReview, b: BookReview): number => {
				if (a.userId == this.userId) {
					return -1;
				}
				if (b.userId == this.userId) {
					return 1;
				}
				if ((a.score || 0) < (b.score || 0)) {
					return 1;
				}
				if ((b.score || 0) < (a.score || 0)) {
					return -1;
				}
				return 0;
			}
		}, {
			id:                            generateId("bookModal-"),
			showArQuizButton:              false,
			showLibraryManagementDisabled: false,
			showGradeLevelInfo:            false,
			bookModificationMessage:       null,
			showBookLastModifiedMessage:   false,
			showAdminGradeMin:             false,
			showRating:                    !isSharedLogin,
			reviewsLoading:                true,
			reviews:                       [],
			book,
			bookLang,
			userLang,
			bookCoverAlt:                  bookLang.ALT_BOOK_COVER_IMG(book.title),
			gradeRangeString:              this.getGradeRangeString(),
			collapseBookDescription:       true,
			similarButtons:                [
				{
					label: this.bookLang.LABEL_RECOMMENDED_GRADE(),
					value: this.getGradeRangeString(),
					href:  this.searchBooksService.getGradeLevelSearchURL(this.book)
				},
				{
					label: this.bookLang.LABEL_PUBLISHER(),
					value: this.book.bookPublisherTitle,
					href:  this.searchBooksService.getBookPublisherSearchURL(this.book)
				},
				{
					label: this.bookLang.LABEL_BOOK_SERIES(),
					value: this.book.bookSeriesTitle,
					href:  this.searchBooksService.getBookSeriesSearchURL(this.book)
				},
				{
					label: this.bookLang.LABEL_AUDIO_TIME(),
					value: this.book.audioRuntimeFriendly,
					href:  this.searchBooksService.getBookAudioRuntimeSearchURL(this.book)
				},
				{
					label: this.bookLang.LABEL_PAGE_COUNT(),
					value: String(this.book.pagecount),
					href:  this.searchBooksService.getBookPageCountSearchURL(this.book)
				},
				{
					label: this.bookLang.LABEL_GUIDED_READING(),
					value: this.book.guidedReadingLevel,
					href:  this.searchBooksService.getBookGuidedReadingLevelSearchURL(this.book)
				},
				{
					label: this.bookLang.LABEL_LEXILE(),
					value: this.book.lexileLabel,
					href:  this.searchBooksService.getSimilarLexileSearchURL(this.book)
				},
				{
					label: this.bookLang.LABEL_ATOS(),
					value: this.book.arQuizLevel,
					href:  this.searchBooksService.getSimilarATOSSearchURL(this.book)
				}
			],
			primaryActions:   [],
			secondaryActions: []
		}) as BookModalView;

		if (this.userRoleId != Role.STUDENT) {
			this.view.dataset.collapseBookDescription = false;
			this.view.modalTable.classList.add("-faculty");
		}

		if (this.view.dataset.book.description.length <= 250) {
			this.view.bookHover.classList.remove("-arrow");
		}
		this.book.onChange(() => {
			this.view.invalidate("book");
		});

		this.tabset = new Tabset(this.view.tabContainer, this.view.tablist, this.view.tabContainer);
		this.tabset.onTabActivate((e) => {
			if (e.tab.tabId == "reviews" && ! this.hasReviewData) {
				this.loadBookReviews().catch(console.error);
			}
			e.tab.tab.classList.add("-active");
			e.tab.tabpanel.classList.remove("-hidden");
		});
		this.tabset.onTabDeactivate(e => {
			e.tab.tab.classList.remove("-active");
			e.tab.tabpanel.classList.add("-hidden");
		});
		this.tabset.add({ tabId: "overview", tab: this.view.overviewButton, tabpanel: this.view.overviewTab });
		this.tabset.add({ tabId: "details", tab: this.view.detailsButton, tabpanel: this.view.detailsTab });
		this.tabset.add({ tabId: "reviews", tab: this.view.reviewsButton, tabpanel: this.view.reviewsTab });
		this.tabset.showTab("overview");
		this.initializeLibraryManagement();
	}

	public showArQuizButton(showArQuizButton: boolean): void {
		this.view.set({ showArQuizButton });
	}

	private initializeLibraryManagement(): void {
		this.view.dataset.bookModificationMessage = null;
		if (!this.userRoleId || this.userRoleId === Role.STUDENT) {
			return;
		}
		this.view.dataset.showRating = false;
		this.view.gradeMin.value = String(this.book.minimumGradeBookAccess || "0");

		if (!this.canManageBookGradeLevelRestrictions) {
			this.view.dataset.showLibraryManagementDisabled = true;
			return;
		}
		if (this.userRoleId === Role.DISTRICT_ADMIN) {
			this.view.dataset.showGradeLevelInfo = true;
			this.view.dataset.showAdminGradeMin = this.canManageBookGradeLevelRestrictions;
			return;
		}
		if (this.userRoleId === Role.BUILDING_ADMIN) {
			const managementEnabled = this.canManageBookGradeLevelRestrictions;
			this.view.dataset.showGradeLevelInfo = Boolean(managementEnabled || this.book.accountMinimumGradeBookAccess);
			this.view.dataset.showAdminGradeMin = managementEnabled;
			this.view.gradeMin.value = String(this.book.minimumGradeBookAccess || "0");
			if (this.book.accountMinimumGradeBookAccess) {
				this.view.dataset.bookModificationMessage = this.getAccessMessage(
					this.book.accountMinimumGradeBookAccess,
					this.book.accountMinGradeBookLastModified || "",
					managementEnabled
				);
			}
			return;
		}

		this.view.dataset.showGradeLevelInfo = true;
		this.view.dataset.showAdminGradeMin = false;
		this.view.dataset.bookModificationMessage = this.getAccessMessage(this.book.minimumGradeBookAccess, this.book.minGradeBookLastModified);
	}

	private getAccessMessage(minimumGradeBookAccess: number|null, lastModifiedMessage: string, isEditable: boolean = false): string {
		const lang = this.bookLang;
		if (minimumGradeBookAccess == 20) {
			return (isEditable ? lang.DESCR_MIN_GRADE_EDITABLE_NO_ACCESS : lang.DESCR_MIN_GRADE_FACULTY_NO_ACCESS)();
		}
		if (minimumGradeBookAccess) {
			if (isEditable) {
				return lang.DESCR_MIN_GRADE_EDITABLE(UserLangGrade(this.userLang, minimumGradeBookAccess));
			}
			return lang.DESCR_MIN_GRADE_FACULTY(UserLangGrade(this.userLang, minimumGradeBookAccess));
		}
		if (lastModifiedMessage) {
			return (isEditable ? lang.DESCR_MIN_GRADE_EDITABLE_ALL_GRADES : lang.DESCR_MIN_GRADE_FACULTY_ALL_GRADES)();
		}
		return lang.DESCR_MIN_GRADE_FACULTY_NO_MIN();
	}

	private async loadBookReviews(): Promise<void> {
		if (this.hasReviewData) {
			return;
		}
		this.hasReviewData = true;
		const reviews = await this.bookReviewService.getReviews(this.book.bookId);
		this.view.set({ reviews, reviewsLoading: false });
	}

	private getGradeRangeString(): string {
		return (this.book.minUserGradeLevelId ? UserLangGradeShort(this.userLang, this.book.minUserGradeLevelId) : "")
			+ " - " +
			(this.book.maxUserGradeLevelId ? UserLangGradeShort(this.userLang, this.book.maxUserGradeLevelId) : "");
	}

	public addAction(action: BookModalAction, primary: boolean = false): HTMLElement {
		if (primary) {
			return (this.view.primaryActions.push(action)).element;
		}

		return (this.view.secondaryActions.push(action)).element;
	}

	get element(): HTMLElement {
		return this.view.element;
	}
}
